import { FC, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { PageTitle } from '../../../_metronic/layout/core';
import { AuthContext } from '../../context/auth/auth.context';
import { database, ref, onValue } from '../../../firebase';

interface EquipmentParameter {
  data_type: string;
  masked_value: string;
  parameter_name: string;
  unit: string;
  updated_at: string;
  value: string;
}

interface EquipmentData {
  equipment_id: number;
  equipment_name: string;
  gateway_hardware_id: string;
  gateway_type_id: number;
  last_updated: string;
  parameters: Record<string, EquipmentParameter>;
}

const LivePage: FC<any> = () => {
  const { userId, organizationId } = useContext(AuthContext);
  const [equipmentData, setEquipmentData] = useState<Record<string, EquipmentData>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const equipmentRef = ref(database, 'equipment');

    const unsubscribe = onValue(equipmentRef, (snapshot) => {
      const data = snapshot.val();
      
      if (data) {
        setEquipmentData(data);
      } else {
        setEquipmentData({});
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className='d-flex align-items-center justify-content-center' style={{ height: '300px' }}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (!Object.keys(equipmentData).length) {
    return (
      <div className='alert alert-info'>
        No equipment data available. Please check back later.
      </div>
    );
  }

  return (
    <>
      <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header'>
              <h3 className='card-title'>Live Equipment Data</h3>
            </div>
            <div className='card-body p-0'>
              <div className='table-responsive'>
                <table className='table table-striped table-row-bordered gy-5 gs-7'>
                  <thead>
                    <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200'>
                      <th>Equipment ID</th>
                      <th>Equipment Name</th>
                      <th>Hardware ID</th>
                      <th>Last Updated</th>
                      <th>Parameters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(equipmentData).map(([key, equipment]) => (
                      <tr key={key}>
                        <td>{equipment.equipment_id}</td>
                        <td>{equipment.equipment_name}</td>
                        <td>{equipment.gateway_hardware_id}</td>
                        <td>{new Date(equipment.last_updated).toLocaleString()}</td>
                        <td>
                          <div className='d-flex flex-column'>
                            {Object.entries(equipment.parameters).map(([paramKey, param]) => (
                              <div key={paramKey} className='mb-2'>
                                <strong>{param.parameter_name}:</strong> {param.value} {param.unit}
                                {param.masked_value !== param.value && (
                                  <span className='text-muted ms-2'>(Masked: {param.masked_value})</span>
                                )}
                                <div className='text-muted small'>
                                  Updated: {new Date(param.updated_at).toLocaleTimeString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const LiveWrapper: FC = () => {
  const intl = useIntl();
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.LIVE' })}</PageTitle>
      <LivePage />
    </>
  );
};

export { LiveWrapper };